import { Link } from "react-router-dom";
import { Search, Megaphone, ArrowUpRight, LogOut } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";

const MODULES = [
  {
    to: "/prospection",
    testid: "module-prospection",
    eyebrow: "Module 01",
    title: "Prospection",
    baseline: "Trouver, scorer et appeler les bonnes entreprises",
    bullets: ["Base SIRENE + Google Places", "Scoring de priorité & lignes directes", "Pipeline, RDV et statistiques"],
    Icon: Search,
    ready: true,
  },
  {
    to: "/commercial",
    testid: "module-commercial",
    eyebrow: "Module 02",
    title: "Suivi commercial",
    baseline: "Transformer les prospects en clients signés",
    bullets: ["Pipeline 10 étapes · opportunités auto", "Rendez-vous qualifiés · audio & IA ready", "Contrats & commissions par téléprospectrice"],
    Icon: Megaphone,
    ready: true,
  },
];

export default function ModuleChooser() {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen bg-brand-paper">
      <div className="max-w-[1200px] mx-auto px-6 sm:px-10 lg:px-16 py-16 sm:py-24">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 sm:gap-5">
            <img src="/picto-instant.png" alt="Agence Instant" className="h-10 w-10 object-contain" data-testid="chooser-picto" />
            <div className="h-10 w-px bg-brand-line" />
            <div className="leading-none">
              <div className="font-display text-2xl sm:text-3xl text-brand-ink">Instant Studio</div>
              <div className="eyebrow mt-1.5">by Agence Instant</div>
            </div>
          </div>
          {user && typeof user === "object" && (
            <div className="flex items-center gap-3">
              <span className="hidden sm:block text-xs text-brand-subtle font-mono" data-testid="current-user-email">{user.email}</span>
              <button
                onClick={logout}
                data-testid="logout-button"
                className="inline-flex items-center gap-1.5 rounded-md border border-brand-line bg-white px-3 py-1.5 text-xs text-brand-ink hover:border-brand-ink/25 transition-colors"
              >
                <LogOut className="h-3.5 w-3.5" strokeWidth={1.5} />
                Déconnexion
              </button>
            </div>
          )}
        </div>

        <h1 className="mt-16 sm:mt-24 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-brand-ink max-w-[22ch]">
          Choisissez votre module
        </h1>
        <p className="mt-5 text-base text-brand-subtle max-w-[52ch]">
          Deux espaces de travail, un seul studio. Sélectionnez celui sur lequel vous travaillez aujourd'hui.
        </p>

        <div className="mt-14 sm:mt-20 grid gap-6 sm:gap-8 lg:grid-cols-2">
          {MODULES.map(({ to, testid, eyebrow, title, baseline, bullets, Icon, ready }, i) => (
            <Link
              key={to}
              to={to}
              data-testid={testid}
              style={{ animationDelay: `${i * 90}ms` }}
              className="group relative block rounded-lg border border-brand-line bg-white p-7 sm:p-9 transition-[border-color,transform,box-shadow] duration-300 hover:border-brand-ink/25 hover:-translate-y-0.5 hover:shadow-card"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="eyebrow">{eyebrow}</div>
                <ArrowUpRight
                  className="h-4 w-4 text-brand-subtle transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-brand-moss"
                  strokeWidth={1.5}
                />
              </div>

              <Icon className="mt-8 h-7 w-7 text-brand-moss" strokeWidth={1.25} />

              <div className="mt-6 font-display text-3xl sm:text-4xl leading-none text-brand-ink">
                Instan.t <span className="text-brand-moss">{title}</span>
              </div>
              <div className="mt-3 text-base text-brand-subtle max-w-[34ch]">{baseline}</div>

              <ul className="mt-7 space-y-2">
                {bullets.map((b) => (
                  <li key={b} className="flex items-baseline gap-2.5 text-sm text-brand-ink/80">
                    <span className="h-1 w-1 rounded-full bg-brand-moss flex-shrink-0 translate-y-[0.45rem]" />
                    {b}
                  </li>
                ))}
              </ul>

              <div className="mt-9 inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.16em] text-brand-subtle">
                <span className={`h-1.5 w-1.5 rounded-full ${ready ? "bg-brand-moss" : "bg-brand-line"}`} />
                {ready ? "Opérationnel" : "En préparation"}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
